import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ApiService } from 'src/app/shared/services/api/api/api.service';
import { TPollyVoice } from 'src/app/shared/services/api/api/TPollyVoice';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { VoiceService } from 'src/app/game/lvl1/voices/voice.service';
import { IVoice } from 'src/app/talent-litmus-core/game-core/level1-core/voice-core';

@Component({
  selector: 'app-polly',
  templateUrl: './polly.component.html',
  styleUrls: ['./polly.component.scss'],
})
export class PollyComponent implements OnInit, OnDestroy {
  private _unsubscribe = new Subject<void>();

  @ViewChild('customContent') private _customContent;
  @ViewChild('voiceModal') private _voiceModal;
  voicesList: TPollyVoice[];
  languages: string[];
  voices: TPollyVoice[];
  audioUrl: any = '';
  previousFetchedData = '';
  modalWindow: NgbModalRef = null;
  fileUploadForm = new FormGroup({
    file: new FormControl('', [Validators.required]),
    fileSource: new FormControl('', [Validators.required]),
  });

  ttsForm = new FormGroup(
    {
      language: new FormControl('', [Validators.required]),
      voice: new FormControl('', [Validators.required]),
      inputText: new FormControl('', [Validators.required]),
      engine: new FormControl('neural', [Validators.required]),
    },
    [this._createVoiceEngineValidator()],
  );

  private _createVoiceEngineValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!(value && this.voicesList)) {
        return null;
      }
      const voice = this.voicesList.find((v) => v.Name === value.voice);

      return voice && !voice.SupportedEngines.includes(value.engine)
        ? { invalidEngine: true }
        : null;
    };
  }

  private _onLanguageSelected(language: string): void {
    this.voices = this.voicesList.filter(
      (voice) => voice.LanguageName === language,
    );
    this.ttsForm.patchValue({ voice: this.voices[0].Name });
  }

  constructor(
    private _apiService: ApiService,
    private _toaster: ToastrService,
    private _sanitizer: DomSanitizer,
    private _modalService: NgbModal,
    private _voiceService: VoiceService,
  ) {}

  ngOnInit(): void {
    this.ttsForm.controls.language.valueChanges
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (language: string) => {
          this._onLanguageSelected(language);
        },
      });

    this._apiService
      .listVoices()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((voiceList) => {
        this.voicesList = voiceList;
        this.languages = Array.from(
          new Set(this.voicesList.map((voice) => voice.LanguageName)),
        );
      });
  }

  sanitize(url: string): SafeUrl {
    return this._sanitizer.bypassSecurityTrustUrl(url);
  }

  downloadAudio(): void {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = this.audioUrl;
    a.download = 'test.mp3';
    document.body.appendChild(a);
    a.click();
    setTimeout((): void => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(this.audioUrl);
    }, 100);
  }

  playAudio(url: string): void {
    const audioElement = document.createElement('audio');
    audioElement.src = url;
    audioElement.play();
  }

  addVoice(): void {
    this._modalService.open(this._voiceModal, {
      windowClass: 'dark-modal',
      backdrop: 'static',
      size: 'lg',
    });
  }

  onVoiceSubmit(form: NgForm): void {
    const query = {
      name: form.value.name,
      url: this.audioUrl,
    };
    this._voiceService
      .addVoice(query)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(
        (response: IVoice) => {
          console.log('Response: ', response);
          this._toaster.success('Voice added successfully');
        },
        (err) => {
          console.log(err);
          this._toaster.error(err?.error?.message);
        },
      );
  }

  submitForm(): void {
    if (!this.ttsForm.valid) {
      this._toaster.error('Invalid input');
      return;
    }

    const data = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Text: this.ttsForm.get('inputText').value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      VoiceId: this.ttsForm.get('voice').value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Engine: this.ttsForm.get('engine').value,
    };

    const currentData = JSON.stringify(data);

    if (this.previousFetchedData === currentData) {
      this.playAudio(this.audioUrl);
      return;
    }

    this._apiService
      .getSpeechFromText(data)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((audioStream) => {
        this.previousFetchedData = JSON.stringify(data);
        const audioData = JSON.parse(audioStream);
        const uInt8Array = new Uint8Array(audioData.AudioStream.data);
        const arrayBuffer = uInt8Array.buffer;
        const blob = new Blob([arrayBuffer]);
        const url = URL.createObjectURL(blob);
        this.audioUrl = url;
        // const audioElement = document.getElementById(
        //   'tss-audio',
        // ) as HTMLAudioElement;
        this.playAudio(url);

        this._toaster.success('Converted successfully');
      });
  }

  toggleModel(): void {
    this.modalWindow = this._modalService.open(this._customContent, {
      windowClass: 'dark-modal',
      backdrop: 'static',
      size: 'lg',
    });
  }

  onFileChange(event): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.fileUploadForm.patchValue({
        fileSource: file,
      });
    }
  }
  submit(): void {
    if (!this.fileUploadForm.valid) {
      this._toaster.error('Please upload file first');
      return;
    }

    //close the modal window
    this.modalWindow.close();

    const formData = new FormData();
    formData.append('file', this.fileUploadForm.get('fileSource').value);
    this._apiService
      .uploadPolyExcelFile(formData)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (resp) => {
          console.log(resp);
          this._toaster.success(
            'File uploaded successfully. SynthesisTask is queued',
          );
        },
        error: (err) => {
          console.log(err);
          this._toaster.error(err.error.message);
        },
      });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
